/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, RocketLaunchIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { objectStorageService } from '../../services/objectStorageService';
import type { PodstackPlanRate, PricingResponse } from '../../types/objectStorage';

interface Props {
  projectId: string;
  // When provided, the parent acts on plan selection (e.g. CreateBucketModal
  // sets state). When omitted, the component is read-only (standalone pricing
  // page mode).
  selectedPlanType?: 'starter' | 'committed';
  selectedCommitmentTB?: number;
  onSelect?: (planType: 'starter' | 'committed', commitmentTB?: number) => void;
}

/** Local single-use helper - formats a USD amount as INR or USD string. */
function fmtPrice(usd: number, rate: number, currency: 'INR' | 'USD'): string {
  if (currency === 'USD') return `$${usd.toFixed(2)}`;
  return `₹${(usd * rate).toFixed(2)}`;
}

export default function BucketPricingSection({
  projectId,
  selectedPlanType,
  selectedCommitmentTB,
  onSelect,
}: Props) {
  const { data: pricing, isLoading } = useQuery<PricingResponse>({
    queryKey: ['object-storage-pricing', projectId],
    queryFn: () => objectStorageService.getPricing(projectId),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');

  if (isLoading || !pricing) {
    return (
      <div className="space-y-3">
        <div className="h-24 rounded-xl bg-gray-800/40 animate-pulse" />
        <div className="h-48 rounded-xl bg-gray-800/40 animate-pulse" />
      </div>
    );
  }

  const starter = pricing.plans.find((p) => p.id === 'starter');
  const committed = pricing.plans.find((p) => p.id === 'committed');
  const rate = pricing.usd_to_inr_rate;

  return (
    <div className="space-y-6">
      {/* Currency toggle */}
      <div className="flex items-center justify-end gap-2 text-xs">
        <span className="text-gray-400">Show prices in</span>
        <div className="inline-flex rounded-md border border-gray-700 overflow-hidden">
          {(['INR', 'USD'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCurrency(c)}
              className={`px-3 py-1 ${
                currency === c
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {starter && (
          <PlanCard
            plan={starter}
            rate={rate}
            currency={currency}
            isSelected={selectedPlanType === 'starter'}
            onClick={onSelect ? () => onSelect('starter') : undefined}
            highlights={[
              `${fmtPrice(starter.tiers?.[0]?.rate_usd_per_tb ?? (800 / rate), rate, currency)}/TB per month`,
              'Billed monthly · no lock-in',
              'Charged per whole TB, upfront',
              'Zero ingress & egress fees',
            ]}
            icon={<RocketLaunchIcon className="w-6 h-6 text-primary-400" />}
          />
        )}
        {committed && (() => {
          const tiers = committed.tiers ?? [];
          return (
            <PlanCard
              plan={committed}
              rate={rate}
              currency={currency}
              isSelected={selectedPlanType === 'committed'}
              onClick={onSelect ? () => onSelect('committed', selectedCommitmentTB ?? 1) : undefined}
              highlights={[
                `${fmtPrice(tiers[0]?.rate_usd_per_tb ?? (700 / rate), rate, currency)}/TB per month`,
                'Lower rate for a committed size',
                'Billed for max(usage, your commitment)',
                'Zero ingress & egress fees',
              ]}
              icon={<ChartBarIcon className="w-6 h-6 text-primary-400" />}
              footer={
                onSelect && selectedPlanType === 'committed' ? (
                  <CommitmentPicker
                    value={selectedCommitmentTB ?? 1}
                    onChange={(tb) => onSelect('committed', tb)}
                  />
                ) : null
              }
            />
          );
        })()}
      </div>
    </div>
  );
}

function PlanCard({
  plan, rate, currency, isSelected, onClick, highlights, icon, footer,
}: {
  plan: PodstackPlanRate;
  rate: number;
  currency: 'INR' | 'USD';
  isSelected?: boolean;
  onClick?: () => void;
  highlights: string[];
  icon: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={`relative text-left rounded-xl border-2 p-4 transition-all w-full ${
        isSelected
          ? 'border-primary-500 bg-primary-500/15 shadow-lg shadow-primary-500/20'
          : interactive
          ? 'border-gray-700 bg-gray-800/60 hover:border-primary-500/60 hover:bg-gray-800 hover:shadow-md'
          : 'border-gray-700 bg-gray-800/40 cursor-default'
      }`}
    >
      {/* Selected badge - clear at-a-glance signal */}
      {isSelected && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-white text-[10px] font-semibold uppercase tracking-wider">
          <CheckIcon className="w-3 h-3" />
          Selected
        </span>
      )}
      {/* "Click to select" hint when not selected and interactive */}
      {interactive && !isSelected && (
        <span className="absolute top-3 right-3 text-[10px] text-gray-400 uppercase tracking-wider">
          Click to select
        </span>
      )}
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <p className={`text-base font-semibold ${isSelected ? 'text-primary-200' : 'text-white'}`}>{plan.name}</p>
      </div>
      <p className="text-xs text-gray-400 mb-3">{plan.tagline}</p>
      <ul className="space-y-1.5 text-xs">
        {highlights.map((h) => (
          <li key={h} className="flex items-start gap-1.5">
            <CheckIcon className={`w-3.5 h-3.5 ${isSelected ? 'text-primary-300' : 'text-primary-400'} mt-0.5 shrink-0`} />
            <span className="text-gray-200">{h}</span>
          </li>
        ))}
      </ul>
      {footer && <div className="mt-3 pt-3 border-t border-gray-700">{footer}</div>}
    </button>
  );
}

function CommitmentPicker({ value, onChange }: { value: number; onChange: (tb: number) => void }) {
  return (
    <div onClick={(e) => e.stopPropagation()} className="space-y-1.5">
      <label className="block text-xs font-medium text-primary-300">
        Choose your committed size
      </label>
      <div className="flex items-stretch gap-2">
        <input
          type="number"
          min={1}
          value={value}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (!Number.isNaN(n) && n >= 1) onChange(n);
          }}
          className="w-24 px-3 py-2 bg-gray-950 border-2 border-primary-500/40 rounded-md text-white font-mono text-base focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
          aria-label="TB commitment"
        />
        <div className="flex items-center px-3 bg-gray-800/60 border-2 border-gray-700 rounded-md text-xs text-gray-300">
          TB
        </div>
      </div>
      <p className="text-[11px] text-gray-400">Minimum 1 TB. You&apos;ll be billed each month, prepaid, for max(usage, this commitment).</p>
    </div>
  );
}
